<template>
  <div>
    <b-modal title="Confirm Purchase Action" v-model="confirmPurchaseModal" @ok="performPurchase" ok-title="Yes"
             cancel-title="No">
      <span>Are you sure you want to purchase {{ editingSelectionIds.length }} assets?</span>
    </b-modal>

    <b-row v-if="hasPurchaseRequests">
      <b-col md="6">
        <b-form-group label="CLIENT / LICENSEE">
          <v-select v-model="licensee"
                    @search="fetchLicensees"
                    :options="licenseesOptions"
                    push-tags
                    taggable
                    :filterable="false">
            <template slot="no-options">
              type to search licensee...
            </template>
            <template slot="option" slot-scope="lic">
              {{ lic.label }}
            </template>
            <template slot="selected-option" slot-scope="lic">
              {{ lic.label }}
            </template>
          </v-select>
        </b-form-group>
      </b-col>
      <b-col md="3">
        <b-form-group label="JOB ID">
          <b-input name="purchase_request[job_id]" v-model="jobId"></b-input>
        </b-form-group>
      </b-col>
      <b-col md="3">
        <b-form-group label="Purchase Order Number">
          <b-input name="purchase_request[po_number]" v-model="poNumber"></b-input>
        </b-form-group>
      </b-col>
    </b-row>

    <div v-if="availableAssets.length" class="my-3" style="max-height: 60vh; overflow-y: auto">

      <div v-for="asset in availableAssets" class="bg-lighter-gray rounded p-2 d-flex mb-1 w-100" style="gap: 10px">

        <div class="d-flex align-items-center" style="gap: 5px" @click.stop="toggleCheckboxIfPossible(asset)">

          <div style="width: 16px; height: 16px"
               class="rounded-circle font-size-sm border border-primary d-flex align-items-center justify-content-center"
               :class="{'bg-primary text-white': editingCheckboxes[asset[idKeyName]], 'bg-white text-white': !editingCheckboxes[asset[idKeyName]]}">
            <i class="fa-solid fa-check"></i>
          </div>


          <div class="img-bg img-square flex-shrink-0 rounded"
               :style="`background-image: url('${asset.thumb_url}'); min-height: 80px; min-width: 80px`">
          </div>
        </div>

        <div class="long-and-truncated">
          <div class="font-weight-semi-bold">{{ asset.name }}</div>
          <div class="text-gray-600 mt-1">Asset ID: {{ asset.reference_number }}</div>
          <div class="text-gray-600">
            <span v-if="asset.premium">Standard Rights Managed</span>
            <span v-else>Standard Royalty Free</span>
            <span> - {{ asset.usage }}</span>
          </div>
        </div>

        <b-select :options="selectOptions(asset)"
                  style="width: 200px"
                  :disabled="selectableOptionsCount(asset) <= 1"
                  v-model="editingSelection[asset[idKeyName]]"/>
      </div>
    </div>
    <div class="my-3" v-else>
      None of the selected assets are eligible for purchase
    </div>

    <div class="d-flex justify-content-between">
      <b-button variant="primary" class="btn-rounded" v-if="showSupportButton" @click="handleIntercom">Contact sales to change
        subscription
      </b-button>
      <div v-else></div>

      <div>
        <b-button variant="outline" class="btn-rounded" @click="$emit('cancel')">Cancel</b-button>
        <b-button variant="primary" class="btn-rounded" :disabled="purchaseDisabled" @click="confirmPurchaseModal = true">
          <span v-if="bulkActionRunning">Please Wait...</span>
          <span v-else>
          Purchase {{ editingSelectionIds.length }} assets
          </span>
        </b-button>
      </div>
    </div>
  </div>
</template>

<script>

import GeneralMixins from "../../../mixins/GeneralMixins.js";
import PulseLoader from 'vue-spinner/src/PulseLoader';
import normalize from "json-api-normalize";
import Attributes from "../../../models/Attributes.js";
import vSelect from "vue-select";

export default {
  props: ['selectedAssets', 'idKey', 'hasPurchaseRequests'],
  mixins: [GeneralMixins],
  components: {PulseLoader, vSelect},

  data() {
    return {
      bulkActionRunning: false,
      idKeyName: this.idKey || 'id',
      editingSelection: {},
      loaded: false,
      loadedAssets: [],
      products: [],
      tokens: {},
      sizeNames: {},
      confirmPurchaseModal: false,
      licensee: null,
      licenseesOptions: [],
      jobId: null,
      poNumber: null
    }
  },

  mounted() {
    if (this.loadedAssets.length > 0) {
      return
    }

    const assetIds = this.selectedAssets.map(el => el[this.idKeyName])

    this.$http.post("/customers/bulk/purchases/data", {asset_ids: assetIds}).then(result => {
      this.loadedAssets = result.body.assets
      this.products = normalize(result.body.products).get(Attributes.productAttributes)
      this.tokens = result.body.count
      this.sizeNames = result.body.size_names
      this.selectDefaultAssets()
      this.$emit('title', this.modalTitle)
    }, rejectionResult => {
      this.showErrorToast(null, rejectionResult.body.errors)
    })
  },

  watch: {
    modalTitle(val) {
      this.$emit('title', val)
    }
  },

  computed: {
    modalTitle() {
      return `<span class="font-weight-semi-bold">Bulk License Purchase</span> | Using <span class="font-weight-semi-bold">${this.totalTokens - this.remainingTokensTotal}</span> of <b>${this.totalTokens} available licenses</b>`
    },

    showSupportButton() {
      return this.remainingTokens.length === 0 || this.remainingTokens.find(t => t.count === 0)
    },

    editingCheckboxes() {
      return Object.fromEntries(this.editingSelectionIds.map(id => [id, true]))
    },

    purchaseDisabled() {
      return this.bulkActionRunning || this.editingSelectionIds.length === 0 || this.remainingTokens.find(t => t.count < 0) || !this.isBrowserFormValid
    },

    isBrowserFormValid() {
      if (this.hasPurchaseRequests) {
        return this.jobId && this.jobId.length > 0 && this.licensee && this.licensee.length > 0
      }

      return true
    },

    editingSelectionIds() {
      if (this.editingSelection) {
        return Object.keys(this.editingSelection).filter(id => this.editingSelection[id])
      }
      return []
    },

    editingSelectionCount() {
      return this.editingSelectionIds.length
    },

    loadedAssetIds() {
      return this.loadedAssets.map(a => a.id)
    },

    availableAssets() {
      return this.selectedAssets.filter(el => this.loadedAssetIds.includes(el[this.idKeyName]))
    },

    remainingTokens() {
      return Object.keys(this.tokens).map(productId => {
        return {
          product: this.products.find(p => p.id === productId),
          count: this.tokens[productId] - this.selectedAssetsIdsByProductId[productId].length
        }
      })
    },

    totalTokens() {
      return Object.keys(this.tokens).map(productId => this.tokens[productId]).reduce((current, total) => {
        return total + (current || 0)
      }, 0)
    },

    remainingTokensTotal() {
      return this.remainingTokens.map(el => el.count).reduce((current, total) => {
        return total + (current || 0)
      }, 0)
    },

    selectedAssetsIdsByProductId() {
      return Object.fromEntries(this.products.map(product => [product.id, Object.keys(this.editingSelection).filter(k => this.editingSelection[k] === product.id)]))
    }
  },

  methods: {
    handleIntercom() {
      if ('undefined' !== typeof Intercom) {
        Intercom('show')
      }
      this.$emit('cancel')
    },

    fetchLicensees(search, loading) {
      loading(true)
      this.searchLicensees(loading, search, this)
    },

    searchLicensees: _.debounce((loading, search, vm) => {
      const url = `/customers/licensees?query=${encodeURIComponent(search)}`
      vm.$http.get(url).then(fulfilled => {
        vm.licenseesOptions = fulfilled.data
        loading(false)
      })
    }, 150),

    toggleCheckboxIfPossible(asset) {
      const isSelected = this.editingCheckboxes[asset[this.idKeyName]]
      if (isSelected) {
        this.editingSelection[asset[this.idKeyName]] = null
      } else {
        const product = this.availableSelectionProducts(asset)[0]
        if (product) {
          this.editingSelection[asset[this.idKeyName]] = product.id
        }
      }
    },

    handleCheckbox(event, asset) {
      const selected = event.target.checked
      console.log(selected);
      if (selected) {
        const product = this.availableSelectionProducts(asset)[0]
        if (product) {
          // console.log('adding');
          this.editingSelection[asset[this.idKeyName]] = product.id
        } else {
          // console.log('adding but removing');
          this.editingSelection[asset[this.idKeyName]] = null
        }
      } else {
        // console.log('removing');
        this.editingSelection[asset[this.idKeyName]] = null
      }
    },
    performPurchase() {
      this.bulkActionRunning = true

      const assetMap = Object.keys(this.editingSelection).map(assetId => {
        return {
          id: assetId,
          product_id: this.editingSelection[assetId]
        }
      }).filter(el => el.product_id)

      const attrs = {
        bulk_purchase: {
          assets: assetMap,
          job_id: this.jobId,
          po_number: this.poNumber,
          licensee: this.licensee
        }
      }

      this.$http.post("/customers/bulk/purchases", attrs).then(fulfilled => {
        this.showToast(`${assetMap.length} assets were purchased successfully!`)
        const licenses = normalize(fulfilled.body).get(Attributes.licenseAttributes);
        this.$emit('finished', licenses)
      }, rejectionResult => {

        this.showErrorToast(null, rejectionResult.body.errors)
      }).then(() => {
        this.bulkActionRunning = false
      })
    },

    selectDefaultAssets() {
      let currentTokens = {...this.tokens}
      let editingSelection = {}

      this.availableAssets.forEach(asset => {
        const products = this.availableProducts(asset)
        const product = products.find(p => currentTokens[p.id] > 0)
        if (product) {
          editingSelection[asset[this.idKeyName]] = product.id
          currentTokens[product.id] -= 1
        } else {
          editingSelection[asset[this.idKeyName]] = null
        }
      })

      this.editingSelection = editingSelection
    },

    getProducts(productIds) {
      return this.products.filter(p => productIds.includes(p.id))
    },

    sizeName(asset, product) {
      const patAssetType = asset.asset_type.toLowerCase()
      const productAssetType = product.product_asset_types.find(pat => pat.asset_type === patAssetType)

      if (!productAssetType || !this.sizeNames[asset.asset_type]) {
        return ""
      }

      return this.sizeNames[asset.asset_type][productAssetType.version_size]
    },

    availableProducts(asset) {
      const loadedAsset = this.loadedAssets.find(a => a.id === asset[this.idKeyName])
      if (!loadedAsset) {
        return []
      }
      return this.getProducts(loadedAsset.products)
    },

    availableSelectionProducts(asset) {
      return this.availableProducts(asset).filter(product => this.productRemainingTokens(product) > 0)
    },

    selectOptions(asset) {
      return [
        ...this.availableProducts(asset).map(ap => {
          return {
            text: this.sizeName(asset, ap),
            value: ap.id,
            disabled: this.productRemainingTokens(ap) === 0
          }
        })
      ]
    },

    selectableOptionsCount(asset) {
      return this.availableProducts(asset).length
    },

    productRemainingTokens(product) {
      const data = this.remainingTokens.find(t => t.product.id === product.id)
      if (!data) {
        return 0
      }
      return data.count
    },


  }
}
</script>

<style scoped lang="scss">
.long-and-truncated {
  flex: 1;

  &, & > * {
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }
}
</style>